import React, { useEffect, useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Chip,
  ChipProps,
  Card, 
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Fade,
  LinearProgress,
  IconButton,
  Drawer,
  AppBar,
  Toolbar,
  InputAdornment,
  Container,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  TablePagination,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import AnalysisIcon from '@mui/icons-material/Analytics';
import InfoIcon from '@mui/icons-material/Info';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SentimentAnalysisIcon from '@mui/icons-material/Psychology';
import botIcon from '../../assets/Chatbot.svg';
// import SmartToyIcon from '../../assets/Chatbot.svg';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { environment } from '../../environments/environment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ReactMarkdown from 'react-markdown';
// Types and Interfaces
interface InputField {
  key: string;
  label: string;
  type: string;
  value: string | string[];
  info: string;
  options?: string[] | Array<{ id: string; label: string }>;
}

interface NewsItem {
  objectType: string;
  docUID: string;
  entries: Array<{
    key: string;
    label: string;
    info: string;
    value: string | string[] | Array<{
      key: string;
      label: string;
      info: string;
      options?: string[];
      value: string;
    }>;
  }>;
}

interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface MessageBubbleProps {
  sender: 'user' | 'bot';
  children?: React.ReactNode;
  sx?: any;
}

// Add GPT API interface
interface GPTMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface GPTResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

interface FormValues extends Record<string, string | string[] | undefined> {
  topic?: string;
  contentType?: string;
  keywords?: string[];
}

interface InsightChipProps {
  label: string;
  backgroundColor?: string;
  textColor?: string;
  size?: 'small' | 'medium';
}

// Add interface for content insight item
interface ContentInsightItem {
  key: string;
  value: string;
  info: string;
  options?: string[];
  label: string;
}

interface NewsResponse {
  objectType: 'DICTIONARY' | 'PARAGRAPH'|'MULTITEXTOPTIONS';
  entries?: Entry[];
  value?: string;
  docUID?: string;
}

interface Entry {
  key: string;
  value: string | string[] | Array<{
    key: string;
    label: string;
    info: string;
    options?: string[];
    value: string;
  }>;
  label: string;
  info: string;
}

interface ResponseItem {
  objectType: string;
  key: string;
  value: any;
}

// Add Project interface
interface Project {
  id: string;
  org_id: string;
  project_name: string;
  project_description: string;
  project_type: string;
  configuration: any;
  source: string;
  active: boolean;
  status: string;
  last_updated_date: string;
}

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(4),
  height: '100%',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
}));

const IconWrapper = styled(Box)({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  backgroundColor: '#2196F3',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ResultCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  },
}));

const StyledChip = styled(Chip)({
  margin: '4px',
  '& .MuiChip-label': {
    fontWeight: 500,
  }
});

const FloatingBotButton = styled(IconButton)({
  position: 'fixed',
  bottom: '24px',
  right: '24px',
  width: '56px',
  height: '56px',
  borderRadius: '28px',
  backgroundColor: '#2196F3',
  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
  '&:hover': {
    backgroundColor: '#1976D2',
  },
  '& svg': {
    color: '#FFFFFF',
    fontSize: '24px',
  },
});

const ChatDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: '400px',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sender',
})<MessageBubbleProps>(({ theme, sender }) => ({
  maxWidth: '70%',
  padding: theme.spacing(1.5, 2),
  borderRadius: sender === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
  backgroundColor: sender === 'user' ? '#E3F2FD' : '#F5F5F5',
  marginLeft: sender === 'user' ? 'auto' : '16px',
  marginRight: sender === 'user' ? '16px' : 'auto',
  marginBottom: theme.spacing(1),
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  position: 'relative',
  fontSize: '0.75rem',
  lineHeight: 1.4,
  '&:hover': {
    transform: 'translateY(-1px)',
    transition: 'transform 0.2s ease',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  }
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  margin: '8px 0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  borderRadius: '12px !important',
  border: '1px solid #E0E0E0',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '8px 0',
    backgroundColor: '#F0F7FF',
    borderColor: '#159DFF',
  },
  '&.selected': {
    backgroundColor: '#F0F7FF',
    borderColor: '#159DFF',
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  borderRadius: '12px',
  // marginBottom: '4px',
  padding: '4px 8px',
  '&.Mui-expanded': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: '#F5F5F5',
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#FFFFFF',
  borderBottomLeftRadius: '12px',
  borderBottomRightRadius: '12px',
}));

const InsightChip = styled(({ backgroundColor, textColor, ...props }: InsightChipProps & Omit<ChipProps, keyof InsightChipProps>) => (
  <Chip {...props} />
))(({ backgroundColor = '#E3F2FD', textColor = '#1976D2' }) => ({
  backgroundColor,
  color: textColor,
  margin: '4px',
  '& .MuiChip-label': {
    fontWeight: 500,
  }
}));

// Add this helper function
const formatPublishedDate = (dateString: string | undefined): string => {
  if (!dateString || dateString === 'NA') return 'Date not available';
  
  // Handle YYYYMMDD format
  if (dateString.length === 8) {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    return new Date(`${year}-${month}-${day}`).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  // Handle other date formats
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Add helper functions
const getEntryValue = (entries: Entry[] | undefined, key: string): string => {
  if (!entries) return '';
  const entry = entries.find(e => e.key === key);
  if (!entry) return '';
  
  const value = entry.value;
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    return value.map(item => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null && 'value' in item) {
        return String(item.value);
      }
      return String(item);
    }).filter(Boolean).join(', ');
  }
  
  return String(value || '');
};

// Add MarkdownMessage component
const MarkdownMessage = ({ text }: { text: string | number | undefined }) => {
  if (text === undefined || text === null) return null;
  
  // Convert to string to ensure ReactMarkdown receives the correct type
  const textString = String(text);
  
  return (
    <ReactMarkdown
      components={{
        strong: ({ children }) => (
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography component="span" sx={{ fontStyle: 'italic' }}>
            {children}
          </Typography>
        ),
        del: ({ children }) => (
          <Typography component="span" sx={{ textDecoration: 'line-through' }}>
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography component="span" sx={{ display: 'inline' }}>
            {children}
          </Typography>
        ),
      }}
    >
      {textString}
    </ReactMarkdown>
  );
};

// Update formatMessageContent function
const formatMessageContent = (message: ChatMessage) => {
  try {
    console.log(message.text);
    
    // If the message is a plain string and not JSON, return it directly
    if (typeof message.text === 'string' && !message.text.startsWith('{')) {
      return <MarkdownMessage text={message.text} />;
    }

    // Parse the message if it's a string, or use it directly if it's already an object
    const data = typeof message.text === 'string' ? JSON.parse(message.text) : message.text;

    // If data is an array, wrap it in an object with a data property
    const formattedData = Array.isArray(data) ? { data } : data;
    
    if (formattedData.data) {
      return (
        <Box sx={{ width: '100%' }}>
          {formattedData.data.map((item: NewsResponse, index: number) => {
            if (item.objectType === 'DICTIONARY') {
              return (
                <StyledAccordion key={index}>
                  <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, color: '#1A2027', fontSize: '0.75rem !important' }}>
                        {getEntryValue(item.entries, 'title') || 'Untitled'}
                      </Typography>
                      {getEntryValue(item.entries, 'publishedDate') !== 'NA' && (
                        <Typography variant="caption" sx={{ color: '#666', ml: 2, whiteSpace: 'nowrap' }}>
                          {formatPublishedDate(getEntryValue(item.entries, 'publishedDate'))}
                        </Typography>
                      )}
                    </Box>
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>
                    {(item.entries || [])
                      .filter(entry => entry.key !== 'publishedDate' && entry.key !== 'title' && entry.value !== 'NA')
                      .map((entry: Entry, entryIndex: number) => {
                        if (Array.isArray(entry.value)) {
                          return (
                            <Box key={entryIndex} sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A2027', mb: 1 }}>
                                {entry.label}
                              </Typography>
                              {entry.value.map((item: string | { value: string }, itemIndex: number) => {
                                const displayText = typeof item === 'object' ? item.value : String(item);
                                return (
                                  <Typography key={itemIndex} variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: '#2196F3', flexShrink: 0 }} />
                                    <MarkdownMessage text={displayText} />
                                  </Typography>
                                );
                              })}
                            </Box>
                          );
                        } else {
                          return (
                            <Box key={entryIndex} sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A2027', mb: 1 }}>
                                {entry.label}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                <MarkdownMessage text={String(entry.value)} />
                              </Typography>
                            </Box>
                          );
                        }
                      })}
                  </StyledAccordionDetails>
                </StyledAccordion>
              );
            } else if (item.objectType === 'PARAGRAPH') {
              return (
                <Typography key={index} variant="body2" sx={{ 
                  whiteSpace: 'pre-wrap', 
                  mb: 2,
                  color: '#666',
                  lineHeight: 1.6
                }}>
                  <MarkdownMessage text={String(item.value)} />
                </Typography>
              );
            }
            return null;
          })}
        </Box>
      );
    }
    return <MarkdownMessage text={typeof message.text === 'string' ? message.text : JSON.stringify(message.text)} />;
  } catch (error) {
    return <MarkdownMessage text={typeof message.text === 'string' ? message.text : JSON.stringify(message.text)} />;
  }
};

const FullScreenContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#FFFFFF',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  padding: '24px'
});

const NewsAnalysis = () => {
  const [inputFields, setInputFields] = useState<InputField[]>([]);
  const [formValues, setFormValues] = useState<FormValues>({
    contentType: "NEWS_ITEM"  // Default value
  });
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [selectedDocUID, setSelectedDocUID] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 1,
      text: "Hello! I'm your AI assistant. How can I help you analyze the news?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isAddNewsDialogOpen, setIsAddNewsDialogOpen] = useState(false);
  const [addNewsFormValues, setAddNewsFormValues] = useState<string>('');
  const [addNewsLoading, setAddNewsLoading] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(4);
  const navigate = useNavigate();
  const [suggestedQuestions, setSuggestedQuestions] = useState([
    { text: "What are the latest market trends?", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
    { text: "Show me bullish news", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
    { text: "Show me bearish news", icon: <TrendingDownIcon sx={{ fontSize: 16 }} /> },
    { text: "What's the impact on stock market?", icon: <MonetizationOnIcon sx={{ fontSize: 16 }} /> },
    { text: "Analyze market sentiment", icon: <SentimentAnalysisIcon sx={{ fontSize: 16 }} /> }
  ]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [addNewsDialog, setAddNewsDialog] = useState(false);
  const [addNewsFields, setAddNewsFields] = useState<InputField[]>([]);
  const [addNewsFormData, setAddNewsFormData] = useState({
    topic: '',
    keywords: []
  });
  const [isSubmittingNews, setIsSubmittingNews] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    project_name: '',
    project_description: '',
    period_start_date: new Date().toISOString().split('T')[0],
    period_end_date: new Date().toISOString().split('T')[0],
    period_type: 'D'
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const [projectError, setProjectError] = useState('');

  useEffect(() => {
    console.log(suggestedQuestions);
  }, [suggestedQuestions]);

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoadingProjects(true);
      try {
        const response = await axios.post<Project[]>(
          `${environment.apiUrl}/workspace/getProjectForOrg`,
          {
            data: {
              org_id: localStorage.getItem("org_id"),
              project_type: ["NEWS_INTL"]
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );
        setProjects(response.data);
        if (response.data.length > 0) {
          setSelectedProject(response.data[0]);
        }
      } catch (error) {
        // Optionally handle error
      } finally {
        setIsLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  const fetchNewsItems = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic: formValues.topic||"",
            keywords: formValues.keywords||[],
            contentType: "NEWS_ITEM",
            campaignId: selectedProject?.id || "",
            // pageSize: rowsPerPage,
            pageNumber: page + 1
          },
          agent: {
            code: "QUERY_NEWS_INTEL"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data.data) {
        setNewsItems(response.data.data);
        setTotalCount(response.data.totalCount || 0);
      } else {
        setNewsItems([]);
        setTotalCount(0);
      }
    } catch (err:any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to fetch news items. Please try again.');
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProject) {
      fetchNewsItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const fetchInputFields = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/agent/getPayload`,
        {
          data: {},
          agent: { code: "QUERY_NEWS_INTEL" }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setInputFields(response.data.data.primaryInput);
    } catch (err:any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to fetch input fields. Please try again.');
      console.error('Error fetching input fields:', err);
    }
  };

  const handleInputChange = (key: string, value: string) => {
    if (key === 'keywords') {
      // Append new keyword to existing array
      setFormValues((prev: FormValues) => ({
        ...prev,
        keywords: [...(prev.keywords || []), value]
      }));
    } else {
      setFormValues((prev: FormValues) => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const handleDeleteKeyword = (keywordToDelete: string) => {
    setFormValues((prev: FormValues) => ({
      ...prev,
      keywords: (prev.keywords || []).filter(keyword => keyword !== keywordToDelete)
    }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const keywords = Array.isArray(formValues.keywords) ? formValues.keywords : [];
      const topic = typeof formValues.topic === 'string' ? formValues.topic : '';
      
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic,
            keywords,
            contentType: "NEWS_ITEM",
            campaignId: selectedProject?.id || "",
            pageSize: rowsPerPage,
            pageNumber: page + 1
          },
          agent: {
            code: "QUERY_NEWS_INTEL"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data.data) {
        setNewsItems(response.data.data);
        setTotalCount(response.data.totalCount || 0);
      } else {
        setNewsItems([]);
        setTotalCount(0);
      }
    } catch (err:any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to search news. Please try again.');
      console.error('Error searching news:', err);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   const hasKeywords = formValues.keywords ? formValues.keywords.length > 0 : false;
  //   if (formValues.topic || hasKeywords) {
  //     handleSearch();
  //   } else {
  //     fetchNewsItems();
  //   }
  // }, [formValues.topic, formValues.keywords]);

  const formatAnalysisContext = (docUID: string | null) => {
    if (!docUID) return '';
    
    const newsItem = newsItems?.find(item => item.docUID === docUID);
    if (!newsItem) return '';
    
    const context = newsItem.entries.map(entry => {
      const value = Array.isArray(entry.value) 
        ? entry.value.join(', ')
        : entry.value;
      return `${entry.label}: ${value}`;
    }).join('\n');
    
    return `Selected News Item:\n${context}`;
  };

  const callCustomAPI = async (userMessage: string, docId: string) => {
    try {
      const payload = {
        data: {
          query: userMessage,
          campaignId: selectedProject?.id || '',
          ...(docId ? { docUID: docId } : {})
        },
        agent: {
          code: docId ? "QUERY_NEWS_ITEM_AND_RESPOND" : "QUERY_NEWS_INTEL_AND_RESPOND"
        },
      };

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Extract the query response from the API's response
      const queryResponse = response.data?.data;

      return queryResponse || 'No relevant response received from the server.';
    } catch (error:any) {
      console.error('Error calling custom API:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
      return 'I encountered an error processing your request. Please try again later.';
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Math.floor(Date.now()),
      text: newMessage.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    try {
      const response = await callCustomAPI(newMessage.trim(), selectedDocId || '');
      console.log('API Response:', response);
      
      const botMessage: ChatMessage = {
        id: Math.floor(Date.now()),
        text: response,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      // Parse the response to find follow-up questions
      const followupItem = response.find((item: ResponseItem) => {
        console.log('Checking item:', item);
        return item.objectType === 'MULTITEXTOPTIONS'
      });

      console.log('Found followup item:', followupItem);

      if (followupItem) {
        const icons = [
          <TrendingUpIcon sx={{ fontSize: 16 }} />,
          <ShowChartIcon sx={{ fontSize: 16 }} />,
          <TrendingDownIcon sx={{ fontSize: 16 }} />,
          <MonetizationOnIcon sx={{ fontSize: 16 }} />,
          <SentimentAnalysisIcon sx={{ fontSize: 16 }} />
        ];

        const newSuggestedQuestions = followupItem.value.map((question: string, index: number) => {
          console.log('Processing question:', question);
          return {
            text: question,
            icon: icons[index % icons.length]
          };
        });

        console.log('Setting new suggested questions:', newSuggestedQuestions);
        setSuggestedQuestions(newSuggestedQuestions);
      } else {
        console.log('No follow-up questions found, resetting to defaults');
        setSuggestedQuestions([
          { text: "What are the latest market trends?", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
          { text: "Show me bullish news", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
          { text: "Show me bearish news", icon: <TrendingDownIcon sx={{ fontSize: 16 }} /> },
          { text: "What's the impact on stock market?", icon: <MonetizationOnIcon sx={{ fontSize: 16 }} /> },
          { text: "Analyze market sentiment", icon: <SentimentAnalysisIcon sx={{ fontSize: 16 }} /> }
        ]);
      }
    } catch (error) {
      console.error('Error in chat:', error);
      const errorMessage: ChatMessage = {
        id: Math.floor(Date.now()),
        text: 'I apologize, but I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleAccordionChange = (docUID: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setSelectedDocUID(isExpanded ? docUID : null);
  };

  const getFieldValue = (field: InputField): string => {
    const value = formValues[field.key];
    if (!value) return '';
    if (field.type === 'MULTITEXT' && Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  };

  const handleAddNews = async () => {
    if (!addNewsFormValues.trim()) {
      setError('Please enter news content');
      return;
    }

    setAddNewsLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            contentText: addNewsFormValues
          },
          agent: {
            code: "GENERATE_MARKET_INTEL_FOR_STOCKS"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      await fetchNewsItems(); // Refresh news list
      setIsAddNewsDialogOpen(false);
      setAddNewsFormValues('');
    } catch (err:any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to add news. Please try again.');
      console.error('Error adding news:', err);
    } finally {
      setAddNewsLoading(false);
    }
  };

  const fetchAddNewsFields = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/agent/getPayload`,
        {
          data: {},
          agent: { code: "INDEX_NEWS_ITEMS" }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );
      setAddNewsFields(response.data.data.primaryInput);
      
      // Populate keywords from selected project
      if (selectedProject && selectedProject.configuration && selectedProject.configuration.keywords) {
        setAddNewsFormData(prev => ({
          ...prev,
          keywords: selectedProject.configuration.keywords || []
        }));
      }
    } catch (error:any) {
      console.error('Error fetching add news fields:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
    }
  };

  const handleSubmitNews = async () => {
    setIsSubmittingNews(true);
    try {
      await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic: addNewsFormData.topic,
            keywords: addNewsFormData.keywords,
            campaignId: selectedProject?.id || ""
          },
          agent: { code: "INDEX_NEWS_ITEMS" }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      // Reset form and close dialog
      setAddNewsFormData({ topic: '', keywords: [] });
      setAddNewsDialog(false);
      
      // Refresh news list
      fetchNewsItems();
    } catch (error:any) {
      console.error('Error submitting news:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
    } finally {
      setIsSubmittingNews(false);
    }
  };

  const handleAddNewsInputChange = (key: string, value: any) => {
    setAddNewsFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCreateProject = async () => {
    setIsSubmittingProject(true);
    setProjectError('');
    try {
      const response = await axios.post(
        `${environment.apiUrl}/workspace/updateCustomProjectForOrg`,
        {
          data: {
            org_id: localStorage.getItem("org_id"),
            source: "10_org",
            project_items: [
              {
                project_name: newProject.project_name,
                project_description: newProject.project_description,
                project_type: "NEWS_INTL",
                configuration: {
                  period_start_date: newProject.period_start_date,
                  period_end_date: newProject.period_end_date,
                  period_type: 'D'
                }
              }
            ]
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data.upsertRef && response.data.upsertRef.length > 0) {
        // Fetch project details
        const projectDetails = await axios.post(
          `${environment.apiUrl}/workspace/getProjectDetails`,
          {
            data: {
              id: response.data.upsertRef[0]
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );
        setProjects(prev => [...prev, projectDetails.data]);
        setSelectedProject(projectDetails.data);
        setIsProjectDialogOpen(false);
        setNewProject({
          project_name: '',
          project_description: '',
          period_start_date: new Date().toISOString().split('T')[0],
          period_end_date: new Date().toISOString().split('T')[0],
          period_type: 'D'
        });
      }
    } catch (error) {
      setProjectError('Failed to create project. Please try again.');
    } finally {
      setIsSubmittingProject(false);
    }
  };

  return (
    <Box sx={{ padding: '10px' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px',
        marginBottom: '24px',
        justifyContent: 'space-between'
      }}>
        {/* Left: Back, icon, title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIosIcon sx={{ fontSize: 18 }} />}
                            onClick={() => {
                  // Check if we have a stored navigation context
                  const context = sessionStorage.getItem('appsNavigationContext');
                  if (context && context.startsWith('industry:')) {
                    // Navigate back to Apps with the stored context
                    navigate('/Apps');
                  } else {
                    // Default navigation to Apps
                    navigate('/Apps');
                  }
                }}
            sx={{
              color: '#2196F3',
              fontWeight: 600,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: '#E3F2FD'
              }
            }}
          >
            Back
          </Button>
          <NewspaperIcon sx={{ fontSize: 24, color: '#2196F3', marginLeft: '24px' }} />
          <Typography variant="h5" sx={{ 
            color: '#131F32', 
            fontWeight: 600, 
            fontSize: '1.25rem',
            background: 'linear-gradient(45deg, #131F32, #2196F3)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            AI News Analysis
          </Typography>
        </Box>
        {/* Right: Dropdown and button inline */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Select Project</InputLabel>
            <Select
              value={selectedProject?.id || ''}
              onChange={e => {
                const proj = projects.find(p => p.id === e.target.value);
                setSelectedProject(proj || null);
              }}
              label="Select Project"
              disabled={isLoadingProjects}
            >
              {projects.map(project => (
                <MenuItem key={project.id} value={project.id}>
                  {project.project_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsProjectDialogOpen(true)}
            sx={{
              backgroundColor: '#2196F3',
              '&:hover': { backgroundColor: '#1976D2' },
              fontWeight: 600
            }}
          >
            Create Project
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Middle Column - News List */}
        {!isFullScreen && (
          <Grid item xs={12} md={8}>
            <StyledPaper sx={{ 
              border: '1px solid #E0E0E0',
              borderRadius: '16px',
              height: 'calc(100vh - 180px)',
              overflow: 'hidden',
              transition: 'box-shadow 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
              }
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 2.5,
                borderBottom: '1px solid #E0E0E0',
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8F9FA 100%)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <NewspaperIcon sx={{ color: '#159DFF', fontSize: 24 }} />
                  <Typography variant="h6" sx={{ 
                    color: '#131F32', 
                    fontWeight: 600,
                    fontSize: '1.125rem'
                  }}>
                    News Items
                       <Chip
                  label={`${newsItems?.length || 0} items`}
                  size="small"
                  sx={{
                    backgroundColor: '#E3F2FD',
                    color: '#1976D2',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    marginLeft: '10px'
                  }}
                />
                  </Typography>
                </Box>
                  <Box>
   
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            // Reset form data first
            setAddNewsFormData({
              topic: '',
              keywords: selectedProject?.configuration?.keywords || []
            });
            fetchAddNewsFields();
            setAddNewsDialog(true);
          }}
          sx={{
            backgroundColor: '#159DFF',
            '&:hover': { backgroundColor: '#0B7AD1' }
          }}
        >
          Fetch Latest News
        
        </Button>
    
               
                </Box>
              </Box>

              <Box sx={{ 
                height: 'calc(100% - 76px)', 
                overflow: 'auto', 
                p: 2.5,
                backgroundColor: '#FAFBFC'
              }}>
                {newsItems?.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px dashed #E0E0E0',
                    py: 8
                  }}>
                    <NewspaperIcon sx={{ 
                      fontSize: 48, 
                      color: '#9E9E9E',
                      opacity: 0.5
                    }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ 
                        color: '#131F32',
                        fontWeight: 600,
                        mb: 1
                      }}>
                        No news items found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748B' }}>
                        Try adjusting your search criteria or check back later
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {newsItems
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((newsItem) => {
                        const contentInsights = newsItem.entries.find(e => e.key === 'contentInsights')?.value;
                        let effectValue = '';
                        let effectHorizon = '';
                        
                        if (Array.isArray(contentInsights)) {
                          const stockEffect = (contentInsights as ContentInsightItem[]).find(item => item.key === 'stock_price_effect');
                          const horizon = (contentInsights as ContentInsightItem[]).find(item => item.key === 'impact_horizon');
                          
                          effectValue = stockEffect?.value || '';
                          effectHorizon = horizon?.value || '';
                        }

                        const getChipColors = (type: string) => {
                          switch(type) {
                            case 'Bullish':
                              return { bgcolor: '#DCFCE7', color: '#15803D' };
                            case 'Bearish':
                              return { bgcolor: '#FEE2E2', color: '#B91C1C' };
                            case 'Short-term':
                              return { bgcolor: '#E0F2FE', color: '#0369A1' };
                            case 'Long-term':
                              return { bgcolor: '#F0F9FF', color: '#0C4A6E' };
                            case 'stock':
                              return { bgcolor: '#FFF7ED', color: '#C2410C' };
                            default:
                              return { bgcolor: '#F8F9FA', color: '#475569' };
                          }
                        };

                        return (
                          <StyledAccordion 
                            key={newsItem.docUID}
                            expanded={expandedPanel === newsItem.docUID}
                            onChange={(event, isExpanded) => {
                              setExpandedPanel(isExpanded ? newsItem.docUID : null);
                              setSelectedDocId(isExpanded ? newsItem.docUID : null);
                            }}
                            className={selectedDocId === newsItem.docUID ? 'selected' : ''}
                            sx={{
                              '&:not(:last-child)': {
                                mb: 2
                              }
                            }}
                          >
                            <StyledAccordionSummary 
                              expandIcon={
                                <ExpandMoreIcon sx={{ 
                                  color: '#159DFF',
                                  transform: expandedPanel === newsItem.docUID ? 'rotate(-180deg)' : 'rotate(0)',
                                  transition: 'transform 0.3s ease'
                                }} />
                              }
                            >
                              <Box sx={{ width: '100%' }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'flex-start',
                                  mb: 1.5
                                }}>
                                  <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 600,
                                    color: '#1A2027',
                                    lineHeight: 1.4,
                                    pr: 2
                                  }}>
                                    {getEntryValue(newsItem.entries, 'title') || 'Untitled'}
                                  </Typography>
                                  {getEntryValue(newsItem.entries, 'publishedDate') !== 'NA' && (
                                    <Typography variant="caption" sx={{ 
                                      color: '#64748B',
                                      whiteSpace: 'nowrap',
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      backgroundColor: '#F1F5F9',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: '4px'
                                    }}>
                                      {formatPublishedDate(getEntryValue(newsItem.entries, 'publishedDate'))}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexWrap: 'wrap', 
                                  gap: 1,
                                  mt: 1
                                }}>
                                  {effectValue && effectValue !== 'NA' && (
                                    <StyledChip 
                                      label={effectValue}
                                      size="small"
                                      sx={{
                                        bgcolor: getChipColors(effectValue).bgcolor,
                                        color: getChipColors(effectValue).color,
                                        fontWeight: 600,
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                  )}
                                  {effectHorizon && effectHorizon !== 'NA' && (
                                    <StyledChip 
                                      label={effectHorizon}
                                      size="small"
                                      sx={{
                                        bgcolor: getChipColors(effectHorizon).bgcolor,
                                        color: getChipColors(effectHorizon).color,
                                        fontWeight: 500,
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                  )}
                                  {getEntryValue(newsItem.entries, 'stockSymbol') !== 'NA' && (
                                    <StyledChip 
                                      label={getEntryValue(newsItem.entries, 'stockSymbol')}
                                      size="small"
                                      sx={{
                                        bgcolor: getChipColors('stock').bgcolor,
                                        color: getChipColors('stock').color,
                                        fontWeight: 600,
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </StyledAccordionSummary>
                            <StyledAccordionDetails>
                              <Box sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2.5,
                                pt: 1
                              }}>
                                {newsItem.entries
                                  .filter(entry => 
                                    entry.key !== 'publishedDate' && 
                                    entry.key !== 'title' && 
                                    entry.value !== 'NA' && 
                                    entry.key !== 'contentInsights' &&
                                    entry.key !== 'orgCode' &&
                                    entry.key !== 'campaignId' &&
                                    entry.key !== 'currentDate' &&
                                    entry.key !== 'publishedBy' &&
                                    entry.key !== 'sourceLink' &&
                                    entry.key !== 'contentClass' &&
                                 
                                    (Array.isArray(entry.value) 
                                      ? entry.value.length > 0 && entry.value.some((item: string | ContentInsightItem) => 
                                          (typeof item === 'object' && item !== null ? item.value !== 'NA' : item !== 'NA')
                                        )
                                      : true
                                    )
                                  )
                                  .map((entry, entryIndex) => {
                                    if (Array.isArray(entry.value)) {
                                      const filteredValues = (entry.value as (string | ContentInsightItem)[]).filter((item: string | ContentInsightItem) =>
                                        typeof item === 'object' && item !== null ? item.value !== 'NA' : item !== 'NA'
                                      );
                                      
                                      if (filteredValues.length === 0) return null;
                                      
                                      return (
                                        <Box key={entryIndex}>
                                          <Typography variant="subtitle2" sx={{ 
                                            fontWeight: 600, 
                                            color: '#1A2027', 
                                            mb: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                          }}>
                                            <Box sx={{
                                              width: 4,
                                              height: 16,
                                              backgroundColor: '#159DFF',
                                              borderRadius: 4
                                            }} />
                                            {entry.label}
                                          </Typography>
                                          <Box sx={{ pl: 2 }}>
                                            {filteredValues.map((item: string | ContentInsightItem, itemIndex: number) => {
                                              const displayText = typeof item === 'object' && item !== null ? item.value : String(item);
                                              return (
                                                <Typography 
                                                  key={itemIndex} 
                                                  variant="body2" 
                                                  sx={{ 
                                                    color: '#475569',
                                                    mb: 1,
                                                    pl: 2,
                                                    position: 'relative',
                                                    '&:before': {
                                                      content: '""',
                                                      position: 'absolute',
                                                      left: 0,
                                                      top: '50%',
                                                      transform: 'translateY(-50%)',
                                                      width: 4,
                                                      height: 4,
                                                      borderRadius: '50%',
                                                      backgroundColor: '#94A3B8'
                                                    }
                                                  }}
                                                >
                                                  <MarkdownMessage text={displayText} />
                                                </Typography>
                                              );
                                            })}
                                          </Box>
                                        </Box>
                                      );
                                    } else {
                                      return (
                                        <Box key={entryIndex}>
                                          <Typography variant="subtitle2" sx={{ 
                                            fontWeight: 600, 
                                            color: '#1A2027', 
                                            mb: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                          }}>
                                            <Box sx={{
                                              width: 4,
                                              height: 16,
                                              backgroundColor: '#159DFF',
                                              borderRadius: 4
                                            }} />
                                            {entry.label}
                                          </Typography>
                                          <Typography variant="body2" sx={{ 
                                            color: '#475569',
                                            pl: 3,
                                            lineHeight: 1.6
                                          }}>
                                            <MarkdownMessage text={entry.value} />
                                          </Typography>
                                        </Box>
                                      );
                                    }
                                  })}
                              </Box>
                            </StyledAccordionDetails>
                          </StyledAccordion>
                        );
                      })}
                    <Box sx={{ 
                      mt: 2, 
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      borderTop: '1px solid #E0E0E0',
                      pt: 2
                    }}>
                      <TablePagination
                        component="div"
                        count={newsItems.length}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                          setRowsPerPage(parseInt(event.target.value, 10));
                          setPage(0);
                        }}
                        rowsPerPageOptions={[4, 5, 10, 25]}
                        sx={{
                          '.MuiTablePagination-select': {
                            backgroundColor: '#F8F9FA',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            border: '1px solid #E0E0E0'
                          },
                          '.MuiTablePagination-selectIcon': {
                            color: '#159DFF'
                          },
                          '.MuiTablePagination-displayedRows': {
                            color: '#64748B',
                            fontWeight: 500
                          },
                          '.MuiTablePagination-actions': {
                            '.MuiIconButton-root': {
                              color: '#159DFF',
                              '&.Mui-disabled': {
                                color: '#CBD5E1'
                              },
                              '&:hover': {
                                backgroundColor: '#E3F2FD'
                              }
                            }
                          }
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </StyledPaper>
          </Grid>
        )}

        {/* Right Column - Chat Panel */}
        {isFullScreen ? (
          <FullScreenContainer>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              mb: 2
            }}>
              <IconButton
                onClick={() => setIsFullScreen(false)}
                sx={{
                  backgroundColor: '#F0F7FF',
                  '&:hover': { backgroundColor: '#E3F2FD' }
                }}
              >
                <FullscreenExitIcon sx={{ color: '#159DFF' }} />
              </IconButton>
            </Box>
            <StyledPaper sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #E0E0E0',
              borderRadius: '16px',
              overflow: 'hidden'
            }}>
              {/* Chat Header */}
              <Box sx={{ 
                borderBottom: '1px solid #E0E0E0',
                display: 'flex',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8F9FA 100%)',
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  flex: 1 
                }}>
                  <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '2px solid #fff'
                  }}>
                    <img src={botIcon} alt="bot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ 
                      color: '#131F32', 
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      AI Assistant
                      <Chip
                        label={selectedDocId && selectedDocId.trim() !== '' ? 'Selected News' : 'All News'}
                        size="small"
                        sx={{
                          backgroundColor: '#E3F2FD',
                          color: '#1976D2',
                          height: '20px',
                          fontSize: '0.7rem'
                        }}
                      />
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.625rem' }}>
                      Online | Ready to assist
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Chat Messages */}
              <Box sx={{ 
                flex: 1, 
                p: 3, 
                overflowY: 'auto',
                backgroundColor: '#FAFBFC',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: '#F5F5F5',
                  borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#E0E0E0',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#BDBDBD'
                  }
                }
              }}>
                {messages.map((message) => (
                  <MessageBubble key={message.id} sender={message.sender}>
                    <Box sx={{ position: 'relative' }}>
                      {formatMessageContent(message)}
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        mt: 0.5,
                        color: '#64748B',
                        fontSize: '0.625rem',
                        textAlign: message.sender === 'user' ? 'right' : 'left',
                        opacity: 0.8
                      }}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </MessageBubble>
                ))}
                {isTyping && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 2,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    width: 'fit-content',
                    marginLeft: '16px',
                    marginBottom: '8px',
                    opacity: 0,
                    animation: 'fadeIn 0.3s ease-in-out forwards',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(10px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    {/* Animated Dots */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.5,
                      ml: 0.5
                    }}>
                      <Box sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#2196F3',
                        animation: 'pulse 1.4s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '40%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }} />
                      <Box sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#2196F3',
                        animation: 'pulse 1.4s ease-in-out infinite 0.2s',
                        '@keyframes pulse': {
                          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '40%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }} />
                      <Box sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#2196F3',
                        animation: 'pulse 1.4s ease-in-out infinite 0.4s',
                        '@keyframes pulse': {
                          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '40%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }} />
                    </Box>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Chat Input Section */}
              <Box sx={{ 
                bgcolor: '#FFFFFF',
                p: 2,
                borderTop: '1px solid #E0E0E0',
              }}>
                {/* Quick Action Chips */}
                <Box sx={{ 
  display: 'flex', 
  gap: 0.25, // Further reduce the gap between chips
  mb: 0.5,   // Smaller margin-bottom
  flexWrap: 'wrap',
  '& .MuiChip-root': {
    transition: 'all 0.2s ease',
    borderRadius: '4px', // Keep smaller border-radius
    height: '20px', // Significantly smaller height
    border: '1px solid #E5E9EF',
    '&:hover': {
      backgroundColor: '#EBF5FF',
      borderColor: '#159DFF',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px rgba(21, 157, 255, 0.1)'
    }
  }
}}>
  {suggestedQuestions.map((question, index) => (
    <Chip
      key={index}
      icon={question.icon}
      label={question.text}
      onClick={() => {
        setNewMessage(question.text);
        handleSendMessage();
      }}
      sx={{
        backgroundColor: '#F8FAFC',
        color: '#334155',
        fontSize: '0.625rem', // Further reduced font size
        fontWeight: 400, // Light font weight for a more compact look
        height: '20px', // Make the chip even smaller
        '& .MuiChip-icon': {
          color: '#159DFF',
          fontSize: '12px', // Reduce icon size
          marginLeft: '4px' // Reduced margin between icon and label
        },
        '& .MuiChip-label': {
          padding: '0 6px' // Tighten label padding further
        },
        '&:active': {
          backgroundColor: '#E6F0F9',
          boxShadow: 'none',
          transform: 'translateY(0)'
        }
      }}
    />
  ))}
</Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#F8F9FA',
                        fontSize: '0.75rem',
                        '& .MuiOutlinedInput-input': {
                          padding: '8px 12px'
                        }
                      }
                    }}
                  />
                  <IconButton 
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    size="small"
                    sx={{
                      backgroundColor: '#2196F3',
                      color: 'white',
                      width: '36px',
                      height: '36px'
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </StyledPaper>
          </FullScreenContainer>
        ) : (
          <Grid item xs={12} md={4}>
            <StyledPaper sx={{ 
              height: 'calc(100vh - 180px)',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #E0E0E0',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Chat Header */}
              <Box sx={{ 
                borderBottom: '1px solid #E0E0E0',
                display: 'flex',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8F9FA 100%)',
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  flex: 1 
                }}>
                  <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '2px solid #fff'
                  }}>
                    <img src={botIcon} alt="bot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ 
                      color: '#131F32', 
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      AI Assistant
                      <Chip
                        label={selectedDocId && selectedDocId.trim() !== '' ? 'Selected News' : 'All News'}
                        size="small"
                        sx={{
                          backgroundColor: '#E3F2FD',
                          color: '#1976D2',
                          height: '20px',
                          fontSize: '0.7rem'
                        }}
                      />
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.625rem' }}>
                      Online | Ready to assist
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => setIsFullScreen(true)}
                  sx={{
                    backgroundColor: '#F0F7FF',
                    '&:hover': { backgroundColor: '#E3F2FD' }
                  }}
                >
                  <FullscreenIcon sx={{ color: '#159DFF' }} />
                </IconButton>
              </Box>

              {/* Chat Messages */}
              <Box sx={{ 
                flex: 1, 
                p: 3, 
                overflowY: 'auto',
                backgroundColor: '#FAFBFC',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: '#F5F5F5',
                  borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#E0E0E0',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#BDBDBD'
                  }
                }
              }}>
                {messages.map((message) => (
                  <MessageBubble key={message.id} sender={message.sender}>
                    <Box sx={{ position: 'relative' }}>
                      {formatMessageContent(message)}
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        mt: 0.5,
                        color: '#64748B',
                        fontSize: '0.625rem',
                        textAlign: message.sender === 'user' ? 'right' : 'left',
                        opacity: 0.8
                      }}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </MessageBubble>
                ))}
                {isTyping && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 2,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    width: 'fit-content',
                    marginLeft: '16px',
                    marginBottom: '8px',
                    opacity: 0,
                    animation: 'fadeIn 0.3s ease-in-out forwards',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(10px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    {/* Animated Dots */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.5,
                      ml: 0.5
                    }}>
                      <Box sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#2196F3',
                        animation: 'pulse 1.4s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '40%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }} />
                      <Box sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#2196F3',
                        animation: 'pulse 1.4s ease-in-out infinite 0.2s',
                        '@keyframes pulse': {
                          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '40%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }} />
                      <Box sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#2196F3',
                        animation: 'pulse 1.4s ease-in-out infinite 0.4s',
                        '@keyframes pulse': {
                          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '40%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }} />
                    </Box>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Chat Input Section */}
              <Box sx={{ 
                bgcolor: '#FFFFFF',
                p: 2,
                borderTop: '1px solid #E0E0E0',
              }}>
                {/* Quick Action Chips */}
                <Box sx={{ 
  display: 'flex', 
  gap: 0.25, // Further reduce the gap between chips
  mb: 0.5,   // Smaller margin-bottom
  flexWrap: 'wrap',
  '& .MuiChip-root': {
    transition: 'all 0.2s ease',
    borderRadius: '4px', // Keep smaller border-radius
    height: '20px', // Significantly smaller height
    border: '1px solid #E5E9EF',
    '&:hover': {
      backgroundColor: '#EBF5FF',
      borderColor: '#159DFF',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px rgba(21, 157, 255, 0.1)'
    }
  }
}}>
  {suggestedQuestions.map((question, index) => (
    <Chip
      key={index}
      icon={question.icon}
      label={question.text}
      onClick={() => {
        setNewMessage(question.text);
        handleSendMessage();
      }}
      sx={{
        backgroundColor: '#F8FAFC',
        color: '#334155',
        fontSize: '0.625rem', // Further reduced font size
        fontWeight: 400, // Light font weight for a more compact look
        height: '20px', // Make the chip even smaller
        '& .MuiChip-icon': {
          color: '#159DFF',
          fontSize: '12px', // Reduce icon size
          marginLeft: '4px' // Reduced margin between icon and label
        },
        '& .MuiChip-label': {
          padding: '0 6px' // Tighten label padding further
        },
        '&:active': {
          backgroundColor: '#E6F0F9',
          boxShadow: 'none',
          transform: 'translateY(0)'
        }
      }}
    />
  ))}
</Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#F8F9FA',
                        fontSize: '0.75rem',
                        '& .MuiOutlinedInput-input': {
                          padding: '8px 12px'
                        }
                      }
                    }}
                  />
                  <IconButton 
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    size="small"
                    sx={{
                      backgroundColor: '#2196F3',
                      color: 'white',
                      width: '36px',
                      height: '36px'
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </StyledPaper>
          </Grid>
        )}
      </Grid>

      {/* Add News Dialog */}
      <Dialog
        open={addNewsDialog}
        onClose={() => {
          setAddNewsDialog(false);
          // Reset form data when dialog closes
          setAddNewsFormData({
            topic: '',
            keywords: []
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Fetch Latest News</Typography>
            <IconButton onClick={() => {
              setAddNewsDialog(false);
              // Reset form data when dialog closes
              setAddNewsFormData({
                topic: '',
                keywords: []
              });
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {addNewsFields.map((field) => (
              <Box key={field.key} sx={{ mb: 2 }}>
                {field.type === 'SINGLETEXT' && field.options ? (
                  <TextField
                    select
                    fullWidth
                    label={field.label}
                    value={addNewsFormData[field.key as keyof typeof addNewsFormData] || ''}
                    onChange={(e) => handleAddNewsInputChange(field.key, e.target.value)}
                  >
                    {field.options.map((option) => {
                      if (typeof option === 'string') {
                        return (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        );
                      } else {
                        return (
                          <MenuItem key={option.id} value={option.label}>
                            {option.label}
                          </MenuItem>
                        );
                      }
                    })}
                  </TextField>
                ) : field.type === 'MULTITEXT' ? (
                  <Box>
                    <TextField
                      fullWidth
                      label={field.label}
                      placeholder="Type and press Enter to add a keyword"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newKeyword.trim()) {
                          e.preventDefault();
                          handleAddNewsInputChange(field.key, [...addNewsFormData.keywords, newKeyword.trim()]);
                          setNewKeyword('');
                        }
                      }}
                 
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {addNewsFormData.keywords.map((keyword, index) => (
                        <Chip
                          key={index}
                          label={keyword}
                          onDelete={() => {
                            const updatedKeywords = addNewsFormData.keywords.filter((_, i) => i !== index);
                            handleAddNewsInputChange(field.key, updatedKeywords);
                          }}
                          sx={{
                            backgroundColor: '#E3F2FD',
                            color: '#1976D2',
                            '& .MuiChip-deleteIcon': {
                              color: '#1976D2',
                              '&:hover': {
                                color: '#1565C0'
                              }
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setAddNewsDialog(false);
              // Reset form data when dialog closes
              setAddNewsFormData({
                topic: '',
                keywords: []
              });
            }}
            sx={{ color: '#64748B' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitNews}
            disabled={isSubmittingNews || !addNewsFormData.topic}
            sx={{
              backgroundColor: '#159DFF',
              '&:hover': { backgroundColor: '#0B7AD1' }
            }}
          >
            {isSubmittingNews ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={isProjectDialogOpen} onClose={() => setIsProjectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={newProject.project_name}
              onChange={e => setNewProject(prev => ({ ...prev, project_name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Project Description"
              value={newProject.project_description}
              onChange={e => setNewProject(prev => ({ ...prev, project_description: e.target.value }))}
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={newProject.period_start_date}
                onChange={e => setNewProject(prev => ({ ...prev, period_start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={newProject.period_end_date}
                onChange={e => setNewProject(prev => ({ ...prev, period_end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
       
            {projectError && <Box sx={{ color: 'red', mt: 1 }}>{projectError}</Box>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsProjectDialogOpen(false)} sx={{ color: '#2196F3' }}>Cancel</Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={isSubmittingProject || !newProject.project_name || !newProject.project_description || !newProject.period_start_date || !newProject.period_end_date}
            sx={{ backgroundColor: '#2196F3', '&:hover': { backgroundColor: '#1976D2' } }}
          >
            {isSubmittingProject ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewsAnalysis;